/**
 * Client-side API wrapper for the PhishTrainer PLAY portal (learner realm).
 *
 * Mirrors the admin `ptFetch` pattern (lib/phishtrainer/admin-api.ts) but for
 * the recipient/portal realm: reads the learner's recipient JWT from
 * localStorage and calls the backend `/v1/phishtrainer/play/*` endpoints
 * DIRECTLY — no Next.js proxy route, no httpOnly cookie.
 *
 * Why localStorage instead of the `pt_player_session` httpOnly cookie:
 * the cookie (SameSite=lax; Secure) is dropped by incognito / Safari-Brave ITP
 * / third-party-cookie blocking, which bounced learners back to the login page
 * intermittently. The admin app already uses the robust localStorage-token
 * pattern; the player now shares it. The redeem/SSO flow still ALSO sets the
 * cookie for backward-compat with any not-yet-migrated proxy route.
 *
 * Tokens are stored under `hf-play:*` keys (distinct from the admin
 * `hf-app:*` keys) so an operator and a learner can coexist in one browser.
 */
import { API_CONFIG } from '@/lib/api/config'

export const PLAY_KEYS = {
  ACCESS: 'hf-play:access-token',
  REFRESH: 'hf-play:refresh-token',
  USER: 'hf-play:user',
} as const

/** The learner identity the player UI renders. Frozen shape — matches what the
 *  `/api/play/auth/verify` + `/session` routes have always returned. */
export interface PlayUser {
  id: string
  email?: string
  name: string
  /** workspaceId — kept as `orgId` for FE back-compat. */
  orgId: string
  isAnonymous: boolean
  isManager?: boolean
  /**
   * The learner's own language as the API says it on sign-in
   * (`recipient.locale`: an interface language, or null when the directory
   * holds none). /play opens in it on a first visit (play-language.ts).
   */
  locale?: string | null
  isImpersonating?: boolean
  /**
   * #277 defence in depth. Set ONLY when this session was minted via admin
   * "Play as this user" — the id of the admin (`stores/auth-store.ts`
   * `AuthUser.id`) who created it. A genuine learner session (redeemed
   * invite / SSO) never sets this. Lets `player-auth-provider.tsx` refuse to
   * trust a cached impersonation blob once the admin who created it is no
   * longer the one signed in on this browser — an impersonated learner
   * session must not outlive the admin session that minted it.
   */
  impersonatedByAdminId?: string
}

interface RedeemResponse {
  accessToken: string
  refreshToken?: string
  expiresIn?: number
  recipient: {
    id: string
    email?: string | null
    firstName?: string | null
    lastName?: string | null
    workspaceId: string
    companyId?: string
    domainId?: string | null
    isManager?: boolean
    /** The learner's own language (see PlayUser.locale). */
    locale?: string | null
  }
}

function beUrl(path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`
  return `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${clean}`
}

// ── Session storage (localStorage) ────────────────────────────────────────────

export function getPlayToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(PLAY_KEYS.ACCESS)
}

export function getPlayUser(): PlayUser | null {
  if (typeof window === 'undefined') return null
  const raw = localStorage.getItem(PLAY_KEYS.USER)
  if (!raw) return null
  try {
    return JSON.parse(raw) as PlayUser
  } catch {
    return null
  }
}

/** Persist the learner session from a redeem/refresh payload. Returns the
 *  derived PlayUser the provider renders.
 *
 *  `opts.impersonatedByAdminId` is set ONLY by the admin "Play as this user"
 *  flow (components/phishtrainer/admin/pt-user-row-actions.tsx) — see #277.
 *  Every other caller (redeem, SSO callback/finalize) is a genuine learner
 *  login and must NOT set it. */
export function setPlaySession(
  data: RedeemResponse,
  opts?: { impersonatedByAdminId?: string },
): PlayUser {
  const r = data.recipient
  const user: PlayUser = {
    id: r.id,
    email: r.email ?? undefined,
    name: `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || r.email || 'Learner',
    orgId: r.workspaceId,
    isAnonymous: false,
    isManager: r.isManager,
    locale: r.locale ?? null,
    impersonatedByAdminId: opts?.impersonatedByAdminId,
    // Derived, not passed. PlayerShell gates its "do not persist this round"
    // behaviour on `sessionUser?.isImpersonating === true`, and nothing ever
    // set the flag — so the skip never fired and a shadowing operator's rounds
    // were POSTed like a real learner's. The server refuses them (the
    // pt_impersonator cookie check in /api/phishtrainer/rounds), so no data was
    // corrupted, but the client believed it was recording and the UI advanced
    // as though it had.
    isImpersonating: opts?.impersonatedByAdminId != null,
  }
  if (typeof window !== 'undefined') {
    localStorage.setItem(PLAY_KEYS.ACCESS, data.accessToken)
    if (data.refreshToken) localStorage.setItem(PLAY_KEYS.REFRESH, data.refreshToken)
    localStorage.setItem(PLAY_KEYS.USER, JSON.stringify(user))
  }
  return user
}

/** Store just the user object (e.g. from a server-set-cookie flow that already
 *  returned the user but not tokens). */
export function setPlayUser(user: PlayUser): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(PLAY_KEYS.USER, JSON.stringify(user))
  }
}

/** Store raw tokens (used by the verify route bridge + SSO callback). */
export function setPlayTokens(accessToken: string, refreshToken?: string): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(PLAY_KEYS.ACCESS, accessToken)
  if (refreshToken) localStorage.setItem(PLAY_KEYS.REFRESH, refreshToken)
}

export function clearPlaySession(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(PLAY_KEYS.ACCESS)
  localStorage.removeItem(PLAY_KEYS.REFRESH)
  localStorage.removeItem(PLAY_KEYS.USER)
}

// ── #277 defence in depth: admin-identity binding for impersonated sessions ──
//
// admin logout() (stores/auth-store.ts) now clears hf-play:* directly, which
// closes the PRIMARY reported bug (admin A impersonates, logs out; admin B
// logs in, clicks Play, and gets A's learner). This section is the SECOND
// half: even when logout is skipped entirely (browser closed without
// signing out, tab killed), an impersonated blob must not be trusted just
// because it is present in localStorage.

/** The admin auth store persists to this localStorage key
 *  (`stores/auth-store.ts` `persist({ name: 'hf-app:auth-store' })`). Read
 *  directly rather than importing the zustand store — this module must stay
 *  independent of the admin store, and zustand's persist rehydration timing
 *  is not guaranteed synchronous relative to when a caller here needs the
 *  answer. */
const ADMIN_AUTH_STORE_KEY = 'hf-app:auth-store'

/** Best-effort read of the CURRENTLY signed-in admin's id in this browser.
 *  Returns null when no admin is signed in, or the persisted blob can't be
 *  parsed. Never a security boundary — purely a UX/defence-in-depth signal;
 *  the backend is the sole authority over what the presented recipient JWT
 *  can do. */
export function getCurrentAdminId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(ADMIN_AUTH_STORE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: { user?: { id?: string } | null } }
    return parsed.state?.user?.id ?? null
  } catch {
    return null
  }
}

/** True when `user` is an impersonation blob minted by an admin who is no
 *  longer the admin currently signed in on this browser (or no admin is
 *  signed in at all). A genuine learner session (no `impersonatedByAdminId`)
 *  is NEVER considered stale by this check — it is independent of any admin
 *  session by design (see the module docstring on operator/learner
 *  coexistence). */
export function isPlaySessionStaleForCurrentAdmin(user: PlayUser): boolean {
  if (!user.impersonatedByAdminId) return false
  return user.impersonatedByAdminId !== getCurrentAdminId()
}

// ── #279: safe deep-link replay target for the /play/auth redirect flow ─────

/**
 * Validate a `returnTo` deep-link target before replaying it after a
 * redeem/SSO login. Only a same-origin RELATIVE path under `/play` (and not
 * back into `/play/auth` itself) is honoured — this value can arrive from a
 * URL query param or from sessionStorage carried across an OAuth redirect,
 * so it must never be treated as a trusted navigation target verbatim. An
 * attacker-supplied `?returnTo=https://evil.example` or `//evil.example`
 * must not turn the play login flow into an open redirect. Falls back to the
 * existing default, `/play`.
 */
/**
 * The GAME — app/play/game/page.tsx, which mounts PlayerShell and starts a
 * round. What a learner who pressed a button labelled "play" is asking for.
 */
export const PLAY_GAME_PATH = '/play/game'

/**
 * The DASHBOARD — app/play/page.tsx. Tier band, leaderboard, recent rounds.
 * Also the SAFE FALLBACK for a rejected returnTo: the one play path guaranteed
 * to exist and to be harmless.
 */
export const PLAY_HOME_PATH = '/play'

/**
 * Where to send the learner after a successful play-portal sign-in.
 *
 * Three cases, deliberately distinct:
 *
 *   no returnTo      → the GAME. They arrived from an invite/reminder CTA that
 *                      said "play"; the dashboard makes them hunt for it.
 *   valid returnTo   → that path. #279's deep-link bounce depends on this.
 *   hostile returnTo → the DASHBOARD, via sanitizePlayReturnPath.
 *
 * The third case must NOT collapse into the first. If a rejected path also
 * landed in the game, the redirect guard's failure mode would be indistinguish-
 * able from its success mode, and nothing would notice when it stopped working.
 */
export function resolvePostSignInPath(
  rawReturnTo: string | null | undefined,
): string {
  const asked = typeof rawReturnTo === 'string' ? rawReturnTo.trim() : ''
  if (!asked) return PLAY_GAME_PATH
  return sanitizePlayReturnPath(asked)
}

export function sanitizePlayReturnPath(path: string | null | undefined): string {
  const fallback = PLAY_HOME_PATH
  if (!path) return fallback
  // Reject absolute URLs (`scheme:...`) and protocol-relative URLs
  // (`//host/...`) — both navigate cross-origin in a browser.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(path)) return fallback
  if (path.startsWith('//')) return fallback
  if (!path.startsWith('/')) return fallback
  // Reject path traversal segments outright rather than trying to resolve
  // them — the browser (not this function) is what ultimately interprets
  // `..`, so refusing it here is the only reliable guarantee.
  if (path.split('?')[0].split('/').includes('..')) return fallback
  if (path !== PLAY_HOME_PATH && !path.startsWith('/play/')) return fallback
  // Never replay back into the auth flow itself (redirect loop).
  if (path.startsWith('/play/auth')) return fallback
  return path
}

const RETURN_TO_STORAGE_KEY = 'hf-play:return-to'

/**
 * Stash the deep-link target in sessionStorage before navigating away to an
 * external OAuth provider (SSO). `window.location.href = authorizationUrl`
 * leaves this origin entirely, so a query param can't survive the round
 * trip — but sessionStorage does, for as long as this stays the same tab AND
 * the same origin. That holds for both SSO return paths: the same-origin
 * callback (this origin IS the registered OAuth callback host) and the
 * cross-origin handoff (bounces back to `/play/auth/finalize` on THIS same
 * origin, per the handoff design in app/play/auth/sso/callback/page.tsx).
 */
export function storePlayReturnTo(path: string | null | undefined): void {
  if (typeof window === 'undefined') return
  const safe = sanitizePlayReturnPath(path)
  if (safe === '/play') {
    sessionStorage.removeItem(RETURN_TO_STORAGE_KEY)
    return
  }
  sessionStorage.setItem(RETURN_TO_STORAGE_KEY, safe)
}

/** Read + clear the stashed deep-link target, re-validating it (defence in
 *  depth — sessionStorage is same-origin but still not a trust boundary). */
export function consumePlayReturnTo(): string {
  if (typeof window === 'undefined') return '/play'
  const raw = sessionStorage.getItem(RETURN_TO_STORAGE_KEY)
  sessionStorage.removeItem(RETURN_TO_STORAGE_KEY)
  return sanitizePlayReturnPath(raw)
}

// ── Redeem (public — the invite link entry) ───────────────────────────────────
//
// `redeemInvite()` was REMOVED (WS1). It had zero callers and posted straight to
// the backend redeem endpoint, bypassing /api/play/auth/verify — which is the
// only place that relays the 403 SSO_REQUIRED body. Any future caller would have
// turned a policy refusal into a bare "Invalid or expired invite link" and left
// the learner with no way in. Redemption goes through the proxy route; the
// accept page (app/play/auth/_player-auth-inner.tsx) is the single entry point.

// ── Authenticated play fetch (recipient JWT → BE /v1/phishtrainer/play/*) ──────

/**
 * In-flight rotation, shared by every waiter.
 *
 * Refresh tokens are SINGLE USE — the backend revokes the presented row before
 * issuing the next pair. A /play page mounts several data hooks at once, so
 * without this dedupe the first 401 rotates the token and every other
 * concurrent 401 re-presents the now-revoked copy, gets "Refresh token
 * revoked", and the client destroys its own session.
 */
let inFlightRefresh: Promise<string | null> | null = null

/** Test seam: drop any memoised in-flight rotation between cases. */
export function __resetPlayRefreshForTests(): void {
  inFlightRefresh = null
}

async function rotatePlayToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null
  const refreshToken = localStorage.getItem(PLAY_KEYS.REFRESH)
  if (!refreshToken) return null
  try {
    const res = await fetch(beUrl('/portal/auth/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      cache: 'no-store',
    })
    if (!res.ok) {
      // The backend REJECTED the token (expired / revoked / unknown): this
      // session is dead and no retry will revive it. Drop it so the player
      // provider bounces to /play/auth instead of leaving the learner on a page
      // whose every request 401s. A network error is NOT a rejection — it falls
      // to the catch below and keeps the session, so a blip can't sign anyone out.
      if (res.status === 401 || res.status === 403) clearPlaySession()
      return null
    }
    const data = (await res.json()) as { accessToken: string; refreshToken?: string }
    setPlayTokens(data.accessToken, data.refreshToken)
    // Mirror the rotated ACCESS token into the httpOnly cookie so server-side
    // paths stop decaying. The cookie is minted once and never renewed, so it
    // used to keep the ORIGINAL token forever: the client stayed healthy while
    // every proxy path that falls back to the cookie died at 8h — which is how a
    // submitted round came to persist nothing.
    //
    // Only the access token is mirrored. The REFRESH token stays single-owner in
    // localStorage on purpose (prod incident 75f3f23d: two owners of a
    // rotate-and-revoke token killed each other's copy).
    //
    // Fire-and-forget: a failed sync must never break the retry this rotation
    // exists to serve — the bearer is the primary credential either way.
    void fetch('/api/play/auth/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken: data.accessToken }),
    }).catch(() => {})
    return data.accessToken
  } catch {
    return null
  }
}

/** Rotate the recipient JWT via the portal refresh endpoint, coalescing
 *  concurrent callers onto one rotation. Returns the fresh access token, or
 *  null when refresh is impossible (caller signs out). */
async function refreshPlayToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null
  if (!inFlightRefresh) {
    inFlightRefresh = rotatePlayToken().finally(() => {
      inFlightRefresh = null
    })
  }
  return inFlightRefresh
}

/**
 * Call a same-origin Next.js play proxy route (e.g. `/api/phishtrainer/library`)
 * but forward the learner's hf-play bearer so the proxy authenticates via the
 * localStorage token instead of the pt_player_session cookie (incognito/ITP
 * safe). The proxy keeps doing its prototype-shape transform; only the auth
 * source changes. Use this for the proxy routes that still do server-side
 * mapping (library / knowledge / gamification / history / taxonomy / settings /
 * rounds); use playFetch() for routes that hit the BE play API 1:1.
 */
export async function pfetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const send = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      ...(init.headers as Record<string, string> | undefined),
    }
    if (token) headers.Authorization = `Bearer ${token}`
    return fetch(path, { ...init, headers })
  }

  const res = await send(getPlayToken())
  // The 8h access token expires long before the 30d refresh token. Without this
  // retry every proxy route answered 401 for the rest of the session, which the
  // routes then laundered into a 502 / an empty round list.
  if (res.status !== 401) return res
  const fresh = await refreshPlayToken()
  return fresh ? send(fresh) : res
}

export class PlayAuthError extends Error {}

/**
 * Authenticated fetch against a `/v1/phishtrainer/play/...` endpoint. `path` is
 * appended to the play prefix unless it begins with `/`. Attaches the learner's
 * Bearer token; on 401 it refreshes once and retries. Returns the raw Response
 * so callers can map the body / handle non-JSON.
 */
export async function playRawFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = path.startsWith('/')
    ? beUrl(path)
    : beUrl(`/phishtrainer/play/${path}`)
  const doFetch = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined ?? {}),
    }
    if (token) headers.Authorization = `Bearer ${token}`
    return fetch(url, { ...init, headers, cache: 'no-store' })
  }

  let res = await doFetch(getPlayToken())
  if (res.status === 401) {
    const fresh = await refreshPlayToken()
    if (fresh) res = await doFetch(fresh)
  }
  return res
}

/** Authenticated play fetch that returns the parsed JSON body. Throws
 *  PlayAuthError on 401 (caller should sign out / re-auth) and Error on other
 *  non-ok responses. */
export async function playFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await playRawFetch(path, init)
  if (res.status === 401) {
    clearPlaySession()
    throw new PlayAuthError('Your session expired — please sign in again.')
  }
  if (!res.ok) {
    let msg = `Request failed (${res.status})`
    try {
      const j = (await res.json()) as { message?: string; error?: string }
      msg = j.message || j.error || msg
    } catch {
      /* keep default */
    }
    throw new Error(msg)
  }
  return (await res.json()) as T
}
